import React, { useState, useRef, useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type GridApi, type GridReadyEvent, type StateUpdatedEvent, type RowClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_cell_info.scoped.scss";

interface State {
  gridApi: GridApi | null,
}

const defState: State = {
  gridApi: null,
};

interface Result {
  infos: any[]
}

const defResult = {
  infos: []
};

interface Info {
  locationId: string,
  disableCellYn: string,
  locationIdName: string,
  cellStatusName: string,
  whName: string,
  skuNoExName: string,
  skuTypeName: string,
  skuSizeName: string,
  skuCellName: string,
  loadRateName: string,
  skuStockInfos: any[]
}

const defInfo = {
  locationId: "",
  disableCellYn: "",
  locationIdName: "",
  cellStatusName: "",
  whName: "",
  skuNoExName: "",
  skuTypeName: "",
  skuSizeName: "",
  skuCellName: "",
  loadRateName: "",
  skuStockInfos: []
};

const validationMsg = [
  {id: "locationId", type: "NotBlank", msg: "CELL 위치를 확인해 주세요."}
];

function PopupCellInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/stock/popup_cell_info";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);
  const gridStateKey4skuStock = "/stock/popup_cell_info.skuStock";
  const gridState4skuStock = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey4skuStock), []);

  const selectedLocationId = useRef<string>("");

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationIdName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CELL상태", field: "cellStatusName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "whName", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "SKU No", field: "skuNoExName", width: 90, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Type", field: "skuTypeName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Size", field: "skuSizeName", width: 95, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Cell", field: "skuCellName", width: 90, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "적재가능률", headerClass: "hd-ta-right", field: "loadRateName", width: 95, cellStyle: {textAlign: "right"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "singleRow" as const,
      checkboxes: false,
      enableClickSelection: true
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowClicked: (e: RowClickedEvent) => {
      showDetail(e.data);
    },
    onRowDataUpdated: () => {
      if (!selectedLocationId.current) return;
      state.gridApi?.forEachNode((node: any) => {
        if (node.data.locationId === selectedLocationId.current) {
          node.setSelected(true);
          state.gridApi?.ensureIndexVisible(node.rowIndex, "middle");
          showDetail(node.data);
        }
      });
    }
  });

  const gridInfo4skuStock = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "번호", field: "no", width: 50, cellStyle: {textAlign: "center"} },
    { headerName: "관리구분", field: "mngType", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4skuStock = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey4skuStock);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function searchInfo() {
    try {
      const data = await kutil.sendGet("/api/stock/cell_info.data?mode=searchDetail", {location12: props.data.location12, searchType: props.data.searchType}, {});
      for (const info of data.infos) {
        info.locationIdName = valueFormatter.locationId(info.locationId);
        info.cellStatusName = info.disableCellYn === "Y" ? "금지CELL" : info.workStatusEquip === "ERROR" ? "오류CELL" : !info.skuNo ? "공CELL" : "";
        info.whName = info.skuNo ? commonCode.getCodeValue("wh", info.wh) : "-";
        info.skuNoExName = info.skuNo ? info.skuNoEx : "-";
        info.skuTypeName = info.skuNo ? commonCode.getCodeValue("skuType", info.skuType) : "-";
        info.skuSizeName = info.skuNo ? commonCode.getCodeValue("skuSize", info.skuSize) : "-";
        info.skuCellName = info.skuNo ? [info.skuCellUseQty, info.skuCellQty].filter(Boolean).join("/") : "-";
        info.loadRateName = info.skuNo ? `${100 - Number(info.stockRate ?? 0)}%` : "-";
      }
      selectedLocationId.current = info.locationId;
      setResult({
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function showDetail(info: any) {
    try {
      const data = await kutil.sendGet("/api/stock/cell_info.data?mode=searchSkuStock", {skuNo: info.skuNo}, {});
      for (const [idx, info] of data.infos.entries()) {
        info.no = idx + 1;
      }
      setInfo(prev => ({
        ...prev,
        ...info,
        skuStockInfos: data.infos,
      }));
    } catch(e) {
      console.log(e);
    }
  }

  async function updateDisableCellYn(disableCellYn: string) {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    const confirmMsg = disableCellYn == "Y" ? "금지CELL설정" : "금지CELL해제";
    if (await kutil.confirm(confirmMsg + "하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqInfo = { ...info, disableCellYn };
      const data = await kutil.sendPost("/api/stock/cell_info.proc?mode=updateDisableCellYn", reqInfo, {
        validationMsg: validationMsg
      });
      if (disableCellYn == "Y") {
        await kutil.alert("설정하였습니다.");
      } else {
        await kutil.alert("해제하였습니다.");
      }
      await props.onRefresh();
      await searchInfo();
      if (props.data.searchType === "USE" || props.data.searchType === "DISABLE") {
        props.onClose();
      } else {
        await showDetail(reqInfo);
      }
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1520px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          CELL 상세 조회
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* CELL 목록 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.infos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  rowSelection={gridOptions.rowSelection}
                  showInfo={false}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                  onRowClicked={gridCallbacks.onRowClicked}
                  onRowDataUpdated={gridCallbacks.onRowDataUpdated}
                />
              </div>
            </AgGridProvider>
            {/*// CELL 목록 */}
            {/* CELL 상세 정보 */}
            <div>
              <div className="table-wrap">
                <table>
                  <colgroup>
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "33%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "33%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th scope="row">CELL 위치</th>
                      <td>{ info.locationIdName }</td>
                      <th scope="row">CELL 상태</th>
                      <td>{ info.cellStatusName }</td>
                    </tr>
                    <tr>
                      <th scope="row">SKU No</th>
                      <td>{ info.skuNoExName }</td>
                      <th scope="row">SKU Type</th>
                      <td>{ info.skuTypeName }</td>
                    </tr>
                    <tr>
                      <th scope="row">SKU Size</th>
                      <td>{ info.skuSizeName }</td>
                      <th scope="row">SKU Cell</th>
                      <td>{ info.skuCellName }</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <AgGridProvider modules={modules}>
                <div className="grid-area">
                  <CustomAgGridReact
                    columnDefs={gridInfo4skuStock}
                    rowData={info.skuStockInfos}
                    defaultColDef={kutil.defaultColDef}
                    initialState={gridState4skuStock}
                    showInfo={false}
                    onStateUpdated={gridCallbacks4skuStock.onStateUpdated}
                  />
                </div>
              </AgGridProvider>
            </div>
            {/*// CELL 상세 정보 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          {info.locationId && info.disableCellYn !== "Y" && auth.write &&
            <Button variant="contained" autoFocus onClick={() => updateDisableCellYn("Y")}>
              금지Cell설정
            </Button>
          }
          {info.locationId && info.disableCellYn === "Y" && auth.write &&
            <Button variant="contained" autoFocus onClick={() => updateDisableCellYn("N")}>
              금지Cell해제
            </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupCellInfo);
